import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Trophy, 
  Users, 
  Target, 
  Award, 
  Loader,
  AlertCircle,
  Star,
  ArrowUp,
  ArrowDown,
  BarChart3,
  TrendingUp,
  Zap,
  Calendar,
  Clock,
  MapPin
} from 'lucide-react';
import API from '../../../api/API';
import './QRRisultati.css';

const QRRisultati = () => {
  const { idEdizione } = useParams();
  const [statistiche, setStatistiche] = useState(null);
  const [partite, setPartite] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (idEdizione) {
      fetchData();
    }
  }, [idEdizione]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch statistiche (classifiche gironi). live() e non full(): il codice si scansiona sul
      // campo, e live() risponde anche per un'edizione tenuta non pubblica.
      const statsResponse = await API.statistiche.live(idEdizione);
      if (statsResponse.isSuccess) {
        setStatistiche(statsResponse.data);
      }

      // Fetch planning data per ottenere le partite
      const planningResponse = await API.planning.public(idEdizione);
      if (planningResponse.isSuccess && planningResponse.data?.partite) {
        // Ordina le partite per data decrescente (più recenti prima)
        const sortedPartite = planningResponse.data.partite
          .filter(partita => partita.squdra1 && partita.squdra2) // Solo partite con entrambe le squadre
          .sort((a, b) => {
            // Ordina per data/ora decrescente
            const dateA = new Date(`${a.data} ${a.ora || '00:00'}`);
            const dateB = new Date(`${b.data} ${b.ora || '00:00'}`);
            return dateB - dateA;
          });
        setPartite(sortedPartite);
      }

    } catch (err) {
      setError(err.message || 'Errore nel caricamento dei dati');
      setStatistiche(null);
      setPartite([]);
    } finally {
      setLoading(false);
    }
  };

  // Funzioni helper per calcolare le partite per squadra (stesse di AStatistiche)
  const getPartitePerSquadra = () => {
    if (!statistiche?.partite || !statistiche?.squadre) return {};
    
    const partitePerSquadra = {};
    
    // Inizializza conteggio per ogni squadra
    statistiche.squadre.forEach(squadra => {
      partitePerSquadra[squadra.nome] = 0;
    });
    
    // Conta le partite per ogni squadra
    statistiche.partite.forEach(partita => {
      if (partita.squdra1 && partitePerSquadra.hasOwnProperty(partita.squdra1)) {
        partitePerSquadra[partita.squdra1]++;
      }
      if (partita.squdra2 && partitePerSquadra.hasOwnProperty(partita.squdra2)) {
        partitePerSquadra[partita.squdra2]++;
      }
    });
    
    return partitePerSquadra;
  };

  const enrichPlayersWithMatches = (players) => {
    const partitePerSquadra = getPartitePerSquadra();
    
    return players.map(player => ({
      ...player,
      partite: partitePerSquadra[player.squadra] || 0
    }));
  };

  // Funzioni per le classifiche (stesse di AStatistiche)
  const getTopMeteMaschi = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['M', 'm', 'maschio', 'MASCHIO', 'male', 'MALE'].includes(g.sesso))
      .sort((a, b) => {
        const meteA = a.mete || 0;
        const meteB = b.mete || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (meteB !== meteA) return meteB - meteA;
        return partiteB - partiteA;
      })
      .slice(0, 5);
  };

  const getTopMeteFemmine = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['F', 'f', 'femmina', 'FEMMINA', 'female', 'FEMALE'].includes(g.sesso))
      .sort((a, b) => {
        const meteA = a.mete || 0;
        const meteB = b.mete || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (meteB !== meteA) return meteB - meteA;
        return partiteA - partiteB;
      })
      .slice(0, 5);
  };

  const getTopScalpiPieniMaschi = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['M', 'm', 'maschio', 'MASCHIO', 'male', 'MALE'].includes(g.sesso))
      .sort((a, b) => {
        const pieniA = a.pieni || 0;
        const pieniB = b.pieni || 0;
        const vuotiA = a.vuoti || 0;
        const vuotiB = b.vuoti || 0;
        
        if (pieniB !== pieniA) return pieniB - pieniA;
        return vuotiA - vuotiB;
      })
      .slice(0, 5);
  };

  const getTopScalpiPieniFemmine = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['F', 'f', 'femmina', 'FEMMINA', 'female', 'FEMALE'].includes(g.sesso))
      .sort((a, b) => {
        const pieniA = a.pieni || 0;
        const pieniB = b.pieni || 0;
        const vuotiA = a.vuoti || 0;
        const vuotiB = b.vuoti || 0;
        
        if (pieniB !== pieniA) return pieniB - pieniA;
        return vuotiA - vuotiB;
      })
      .slice(0, 5);
  };

  const getTopAssistMaschi = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['M', 'm', 'maschio', 'MASCHIO', 'male', 'MALE'].includes(g.sesso))
      .sort((a, b) => {
        const assistA = a.assist || 0;
        const assistB = b.assist || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (assistB !== assistA) return assistB - assistA;
        return partiteA - partiteB;
      })
      .slice(0, 5);
  };

  const getTopAssistFemmine = () => {
    if (!statistiche?.giocatori) return [];
    
    const playersWithMatches = enrichPlayersWithMatches(statistiche.giocatori);
    
    return playersWithMatches
      .filter(g => ['F', 'f', 'femmina', 'FEMMINA', 'female', 'FEMALE'].includes(g.sesso))
      .sort((a, b) => {
        const assistA = a.assist || 0;
        const assistB = b.assist || 0;
        const partiteA = a.partite || 0;
        const partiteB = b.partite || 0;
        
        if (assistB !== assistA) return assistB - assistA;
        return partiteA - partiteB;
      })
      .slice(0, 5);
  };

  // Formatta la data per la visualizzazione
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  // Formatta l'ora
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5); // Prende solo HH:MM
  };

  const QRStatCard = ({ 
    title, 
    icon: Icon, 
    iconClass, 
    players, 
    primaryStat, 
    primaryLabel,
    secondaryStat, 
    secondaryLabel,
    primaryDesc,
    secondaryDesc 
  }) => (
    <div className={`qr-stat-card ${iconClass}`}>
      <div className="qr-stat-header">
        <div className="qr-stat-icon-container">
          <Icon className={`qr-stat-icon ${iconClass}`} />
        </div>
        <div className="qr-stat-title-container">
          <h3 className="qr-stat-title">{title}</h3>
          <div className="qr-stat-criteria">
            <span className="criteria-item">
              {primaryDesc} <ArrowDown className="sort-icon" />
            </span>
            <span className="criteria-divider">•</span>
            <span className="criteria-item">
              {secondaryDesc} {secondaryDesc.includes('crescente') ? 
                <ArrowUp className="sort-icon" /> : <ArrowDown className="sort-icon" />}
            </span>
          </div>
        </div>
      </div>
      
      <div className="qr-stat-list">
        {players && players.length > 0 ? (
          players.map((player, index) => (
            <div key={`${player.id || index}-${player.nome}-${player.cognome}`} className="qr-stat-item">
              <div className={`position-badge ${index < 3 ? `pos-${index + 1}` : 'pos-other'}`}>
                {index + 1}
              </div>
              
              <div className="player-info">
                <h4 className="player-name">
                  {player.nome} {player.cognome}
                </h4>
                <p className="player-team">
                  {player.squadra || player.maglia || 'N/A'}
                  {player.numero && ` • #${player.numero} ${player.maglia}`}
                </p>
              </div>
              
              <div className="player-stats">
                <div className="stat-primary">
                  <span className="stat-value">{player[primaryStat] || 0}</span>
                  <span className="stat-label">{primaryLabel}</span>
                </div>
                <div className="stat-secondary">
                  <span className="stat-value">{player[secondaryStat] || 0}</span>
                  <span className="stat-label">{secondaryLabel}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-players-data">
            <Users className="no-data-icon" />
            <span>Nessun giocatore trovato</span>
          </div>
        )}
      </div>
    </div>
  );

  const MatchCard = ({ partita, index }) => (
    <div className="match-card">
      <div className="match-header">
        <div className="match-date-time">
          <Calendar className="date-icon" />
          <span className="match-date">{formatDate(partita.data)}</span>
          {partita.ora && (
            <>
              <Clock className="time-icon" />
              <span className="match-time">{formatTime(partita.ora)}</span>
            </>
          )}
        </div>
        {partita.campo && (
          <div className="match-field">
            <MapPin className="field-icon" />
            <span>Campo {partita.campo}</span>
          </div>
        )}
      </div>

      <div className="match-teams">
        <div className="team team-home">
          <span className="team-name">{partita.squdra1}</span>
          {partita.r1 !== undefined && partita.r1 !== null && (
            <span className="team-score">{partita.r1}</span>
          )}
        </div>
        
        <div className="match-separator">VS</div>
        
        <div className="team team-away">
          {partita.r2 !== undefined && partita.r2 !== null && (
            <span className="team-score">{partita.r2}</span>
          )}
          <span className="team-name">{partita.squdra2}</span>
        </div>
      </div>

      {partita.fase && (
        <div className="match-phase">
          <span className="phase-badge">{partita.fase}</span>
          {partita.girone && <span className="group-badge">Girone {partita.girone}</span>}
        </div>
      )}
    </div>
  );

  const LoadingSpinner = () => (
    <div className="qr-loading">
      <Loader className="qr-loading-icon" />
      <h3 className="qr-loading-title">Caricamento risultati...</h3>
      <p className="qr-loading-text">Attendere prego</p>
    </div>
  );

  const ErrorMessage = () => (
    <div className="qr-error">
      <AlertCircle className="qr-error-icon" />
      <h3 className="qr-error-title">Errore nel caricamento</h3>
      <p className="qr-error-text">{error}</p>
      <button 
        onClick={fetchData}
        className="qr-retry-button"
      >
        Riprova
      </button>
    </div>
  );

  return (
    <div className="qr-container">
      {/* Header */}
      <div className="qr-header">
        <div className="qr-header-content">
          <BarChart3 className="qr-header-icon" />
          <div>
            <h1 className="qr-title">Risultati Torneo</h1>
            <p className="qr-subtitle">Classifiche giocatori e partite</p>
            {statistiche?.torneo && (
              <p className="qr-tournament-name">{statistiche.torneo}</p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage />
      ) : !statistiche ? (
        <div className="qr-no-data">
          <TrendingUp className="no-data-icon" />
          <h3>Nessun dato disponibile</h3>
          <p>Non ci sono dati disponibili per questa edizione</p>
        </div>
      ) : (
        <div className="qr-content">
          {/* Classifiche Giocatori */}
          <div className="qr-section">
            <h2 className="section-title">
              <Star className="section-icon" />
              Classifiche Giocatori - Top 5
            </h2>
            <div className="qr-stats-grid">
              <QRStatCard
                title="Migliori Realizzatori"
                icon={Award}
                iconClass="realizzatori-m"
                players={getTopMeteMaschi()}
                primaryStat="mete"
                primaryLabel="mete"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Mete decrescente"
                secondaryDesc="Partite decrescente"
              />

              <QRStatCard
                title="Migliori Realizzatrici"
                icon={Award}
                iconClass="realizzatrici-f"
                players={getTopMeteFemmine()}
                primaryStat="mete"
                primaryLabel="mete"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Mete decrescente"
                secondaryDesc="Partite crescente"
              />

              <QRStatCard
                title="Migliori Scalpatori"
                icon={Target}
                iconClass="scalpatori-m"
                players={getTopScalpiPieniMaschi()}
                primaryStat="pieni"
                primaryLabel="pieni"
                secondaryStat="vuoti"
                secondaryLabel="vuoti"
                primaryDesc="Scalpi pieni decrescente"
                secondaryDesc="Scalpi vuoti crescente"
              />

              <QRStatCard
                title="Migliori Scalpatrici"
                icon={Target}
                iconClass="scalpatrici-f"
                players={getTopScalpiPieniFemmine()}
                primaryStat="pieni"
                primaryLabel="pieni"
                secondaryStat="vuoti"
                secondaryLabel="vuoti"
                primaryDesc="Scalpi pieni decrescente"
                secondaryDesc="Scalpi vuoti crescente"
              />

              <QRStatCard
                title="Migliori Assistmen"
                icon={Zap}
                iconClass="assistmen-m"
                players={getTopAssistMaschi()}
                primaryStat="assist"
                primaryLabel="assist"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Assist decrescente"
                secondaryDesc="Partite crescente"
              />

              <QRStatCard
                title="Migliori Assistwoman"
                icon={Zap}
                iconClass="assistwoman-f"
                players={getTopAssistFemmine()}
                primaryStat="assist"
                primaryLabel="assist"
                secondaryStat="partite"
                secondaryLabel="partite"
                primaryDesc="Assist decrescente"
                secondaryDesc="Partite crescente"
              />
            </div>
          </div>

          {/* Feed Partite */}
          <div className="qr-section">
            <h2 className="section-title">
              <Trophy className="section-icon" />
              Partite Giocate
            </h2>
            <div className="matches-feed">
              {partite && partite.length > 0 ? (
                partite.map((partita, index) => (
                  <MatchCard key={`${partita.id || index}-${partita.squdra1}-${partita.squdra2}`} partita={partita} index={index} />
                ))
              ) : (
                <div className="no-matches-data">
                  <Trophy className="no-data-icon" />
                  <h3>Nessuna partita disponibile</h3>
                  <p>Non ci sono partite da mostrare per questa edizione</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRRisultati;